import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Form, Button, Row, Col, Spinner, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWaterReceiptsByHouseThunk,
  createReceiptThunk,
  deleteReceiptThunk,
  clearReceiptsError,
} from "../../../../store/slices/receiptsSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import "../../../../css/master-common.css";

const formatIndianDateTime = (v) => {
  const s = String(v || "").trim();
  if (!s) return "-";

  const onlyDate = /^\d{4}-\d{2}-\d{2}$/.test(s);
  if (onlyDate) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }

  const isoLike = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(isoLike);

  if (Number.isNaN(d.getTime())) {
    const ymd = s.split(" ")[0];
    const [y, m, day] = String(ymd).split("-");
    if (y && m && day) return `${day}-${m}-${y}`;
    return s;
  }

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  let hh = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12;
  hh = hh === 0 ? 12 : hh;

  return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
};

// Panchayath name shown for a receipt row, by receipt type:
// SLRM receipt -> SLRM Panchayath name, otherwise Panchayath name.
const rowPanName = (r) => {
  if (r?.receipt_pan_name) return r.receipt_pan_name;
  const type = String(r?.rct_type || "").trim().toLowerCase();
  if (type === "slrm") return r?.slrm_pan_name || r?.pan_name || "";
  return r?.pan_name || "";
};

// Keep amounts numeric in exported Excel files.
const excelNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
};

const excelDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const formatted = formatIndianDateTime(value);
  return formatted === "-" ? "" : formatted;
};

const confirmToast = (
  message,
  { yesText = "Delete", noText = "Cancel", onYes, onNo } = {}
) => {
  toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>{message}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            {yesText}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            {noText}
          </button>
        </div>
      </div>
    ),
    { autoClose: false, closeOnClick: false, closeButton: false, draggable: false }
  );
};

export default function ReceiptsTab({ home_id, activeTab, onRefreshProfile }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list = [], loading, saving, deleting, error } = useSelector((s) => s.receipts);
  const token = useSelector((s) => s.auth?.token);

  const perms = useSelector((s) => s.permissions?.map?.house_master_water_receipt) || {};
  const canCreate = !!(perms.can_create ?? perms.can_add);
  const canDelete = !!perms.can_delete;

  const [q, setQ] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [form, setForm] = useState({
    amount: "",
    rct_date: "",
    mode: "Cash",
    remarks: "",
    man_rct_no: "",
  });

  const isActive = activeTab === "water_receipts";

  const refresh = async () => {
    if (!home_id) return;
    setRefreshing(true);

    const r = await dispatch(fetchWaterReceiptsByHouseThunk(home_id));

    setRefreshing(false);
    if (fetchWaterReceiptsByHouseThunk.rejected.match(r)) {
      toast.error(String(r?.payload || r?.error?.message || "Failed to refresh"));
    } else {
      toast.success("Refreshed ✅");
    }
  };

  useEffect(() => {
    if (!isActive) return;
    if (!home_id) return;
    dispatch(fetchWaterReceiptsByHouseThunk(home_id));
  }, [isActive, home_id, dispatch]);

  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearReceiptsError());
  }, [error, dispatch]);

  const filtered = useMemo(() => {
    const query = String(q || "").toLowerCase().trim();
    if (!query) return list || [];

    return (list || []).filter((r) => {
      const hay = [
        r.rct_no,
        rowPanName(r),
        r.qr_code,
        r.door_no,
        r.family_head,
        r.ward_name,
        r.rct_type,
        r.mode,
        r.rct_date,
        r.man_rct_no,
        r.amount,
        r.water_fee,
        r.slrm_fee,
        r.remarks,
        r.user_type,
        r.office_username,
        r.agent_name,
        r.generated_name,
        r.generated_on,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(query);
    });
  }, [list, q]);

  // Excel export uses the currently filtered list; no extra API call.
  const exportExcel = () => {
    if (!filtered.length) {
      toast.info("No Water receipts to export");
      return;
    }
    try {
      const data = filtered.map((r) => ({
        "Receipt No": r.rct_no ?? "",
        "Panchayath": rowPanName(r),
        "Name": r.family_head || "",
        "Total Amount": excelNumber(r.amount),
        "House Code": r.qr_code || "",
        "Door No": r.door_no || "",
        "Water Fee": excelNumber(r.water_fee),
        "SLRM Fee": excelNumber(r.slrm_fee),
        "Generated On": excelDate(r.generated_on),
        "Generated By": r.generated_name || r.generated_by || "",
        "Ward": r.ward_name || "",
        "Rct Type": r.rct_type || "",
        "Manual RCT No": r.man_rct_no || "",
        "User Type": r.user_type || "",
        "Office Username": r.office_username || "",
        "Agent Name": r.agent_name || "",
        "Remarks": r.remarks || "",
        "Receipt Date": excelDate(r.rct_date),
        "Mode": r.mode || "",
      }));
      const worksheet = XLSX.utils.json_to_sheet(data);
      worksheet["!cols"] = Object.keys(data[0]).map((heading) => ({
        wch: Math.min(38, Math.max(15, heading.length + 3)),
      }));
      worksheet["!autofilter"] = { ref: worksheet["!ref"] };
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Water Receipts");
      XLSX.writeFile(workbook, `Water_Receipts_House_${home_id}.xlsx`);
      toast.success("Water receipts exported successfully");
    } catch (err) {
      console.error("Water receipt Excel export failed:", err);
      toast.error("Could not export Water receipts");
    }
  };

const submit = async (e) => {
  e.preventDefault();

  if (!canCreate) return toast.error("No Create permission");
  if (!home_id) return toast.warning("Home ID missing");

  const amt = Number(form.amount);
  if (!Number.isFinite(amt) || amt <= 0) {
    return toast.warning("Valid amount required");
  }

  const r = await dispatch(
    createReceiptThunk({
      home_id,
      ...form,
    })
  );

  if (createReceiptThunk.fulfilled.match(r)) {
    toast.success("Receipt created ✅");

    setForm({
      amount: "",
      rct_date: "",
      mode: "Cash",
      remarks: "",
      man_rct_no: "",
    });

    dispatch(fetchWaterReceiptsByHouseThunk(home_id));
    onRefreshProfile?.();
  } else {
    const msg =
      r?.payload?.message ||
      r?.payload ||
      r?.error?.message ||
      "Create failed";

    toast.error(String(msg));
  }
};

const doDelete = async (rct_no) => {
  const x = await dispatch(deleteReceiptThunk({ rct_no, home_id }));

  if (deleteReceiptThunk.fulfilled.match(x)) {
    toast.success(`Receipt #${rct_no} deleted 🗑️`);

    dispatch(fetchWaterReceiptsByHouseThunk(home_id));
    onRefreshProfile?.();
  } else {
    const msg =
      x?.payload?.message ||
      x?.payload ||
      x?.error?.message ||
      "Delete failed";

    toast.error(String(msg));
  }
};

const remove = (rct_no) => {
  if (!canDelete) return toast.error("No Delete permission");

  confirmToast("Delete this receipt?", {
    onYes: () => doDelete(rct_no),
  });
};
  const onPrint = (rct_no) => {
    navigate(`/receipt/PrintReceipt/${rct_no}`);
  };

  const onDownload = async (rct_no) => {
    try {
      setDownloading(String(rct_no));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/receipts/print_receipt_pdf/${rct_no}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download receipt PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt_${rct_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
      toast.success(`Receipt #${rct_no} downloaded ✅`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Download failed");
    } finally {
      setDownloading("");
    }
  };

  if (!isActive) return null;

  return (
    <div className="hm-form-page receipts-tab">
      <Row className="g-3">
        <Col xs={12}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="receipts-head">
                <div className="receipts-head-left">
                  <h5 className="mb-1 hm-form-title">Make Receipt</h5>
                  <div className="hm-form-subtitle">Create a new payment receipt for this house.</div>
                </div>

                {home_id ? (
                  <Badge bg="light" text="dark" className="hm-form-badge">
                    Home ID: <span className="hm-form-badge-val">{home_id}</span>
                  </Badge>
                ) : null}
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              <Form onSubmit={submit}>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">
                        Amount <span className="hm-req">*</span>
                      </Form.Label>
                      <Form.Control
                        className="hm-control"
                        type="number"
                        step="0.01"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        required
                        placeholder="Enter amount"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Receipt Date</Form.Label>
                      <Form.Control
                        className="hm-control"
                        type="date"
                        value={form.rct_date}
                        onChange={(e) => setForm({ ...form, rct_date: e.target.value })}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Payment Mode</Form.Label>
                      <Form.Select
                        className="hm-control"
                        value={form.mode}
                        onChange={(e) => setForm({ ...form, mode: e.target.value })}
                      >
                        <option value="Cash">Cash</option>
                        <option value="UPI">UPI</option>
                        <option value="Card">Card</option>
                        <option value="Bank">Bank</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Manual Receipt No</Form.Label>
                      <Form.Control
                        className="hm-control"
                        type="text"
                        value={form.man_rct_no}
                        onChange={(e) => setForm({ ...form, man_rct_no: e.target.value })}
                        placeholder="Optional"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={9}>
                    <Form.Group className="hm-field">
                      <Form.Label className="hm-label">Remarks</Form.Label>
                      <Form.Control
                        className="hm-control"
                        as="textarea"
                        rows={2}
                        value={form.remarks}
                        onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        placeholder="Optional notes"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3} className="d-flex align-items-end justify-content-end">
                    <div className="w-100">
                      <div className="hm-help mb-2">
                        Fields marked <span className="hm-req">*</span> are required.
                      </div>

                      <Button
                        type="submit"
                        className="hm-primary-btn w-100"
                        disabled={saving || !home_id || !canCreate}
                      >
                        {saving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          "Save Receipt"
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="hm-form-title-wrap receipts-head">
                <div className="receipts-head-left">
                  <h5 className="mb-1 hm-form-title">Receipt List</h5>
                  <div className="hm-form-subtitle">Print, download or delete water receipts created for this house.</div>
                </div>

                <div className="receipts-head-right">
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="master-btn"
                    onClick={exportExcel}
                    disabled={loading || !filtered.length}
                  >
                    Export Excel
                  </Button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="master-btn"
                    onClick={refresh}
                    disabled={loading || refreshing || !home_id}
                    title="Refresh"
                  >
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </Button>

                  <Badge bg="light" text="dark" className="hm-form-badge receipts-total ms-2">
                    Total: <span className="hm-form-badge-val">{filtered.length}</span>
                  </Badge>

                  <Form.Control
                    className="hm-control receipts-search ms-2"
                    placeholder="Search receipt no / name / ward..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              {loading ? (
                <div className="hm-form-loading">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading water receipts...</div>
                </div>
              ) : (
                <div className="master-table-card">
                  <div className="master-table-wrap">
                    <Table
                      bordered
                      hover
                      size="sm"
                      
                      className="mb-0 master-eq-table master-sticky master-row-hover receipt-priority-table"
                    >
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Action</th>
                          <th>Receipt No</th>
                          <th>Panchayath</th>
                          <th>Name</th>
                          <th>Total Amount</th>
                          <th>House Code</th>
                          <th>Door No</th>
                          <th>Water Fee</th>
                          <th>SLRM Fee</th>
                          <th>Generated On</th>
                          <th>Generated By</th>
                          <th>Ward</th>
                          <th>Rct Type</th>
                          <th>Manual RCT No</th>
                          <th>User Type</th>
                          <th>Office Username</th>
                          <th>Agent Name</th>
                          <th>Remarks</th>
                          <th>Receipt Date</th>
                          <th>Mode</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filtered.length === 0 ? (
                          <tr><td colSpan={21} className="text-center text-muted py-4">No water receipts found</td></tr>
                        ) : (
                          filtered.map((r, idx) => (
                            <tr key={r.rct_no}>
                              <td>{idx + 1}</td>
                              <td className="receipt-actions-cell">
                                <Button size="sm" variant="primary" className="me-2 master-btn" onClick={() => onPrint(r.rct_no)}>
                                  Print
                                </Button>
                                <Button
                                  size="sm" variant="outline-success" className="me-2 master-btn"
                                  disabled={String(downloading) === String(r.rct_no)}
                                  onClick={() => onDownload(r.rct_no)}
                                >
                                  {String(downloading) === String(r.rct_no) ? "Downloading..." : "Download"}
                                </Button>
                                <Button
                                  size="sm" variant="outline-danger" className="master-btn"
                                  disabled={String(deleting) === String(r.rct_no) || !canDelete}
                                  onClick={() => remove(r.rct_no)}
                                >
                                  {String(deleting) === String(r.rct_no) ? "Deleting..." : "Delete"}
                                </Button>
                              </td>
                              <td>{r.rct_no}</td>
                              <td>{rowPanName(r) || "-"}</td>
                              <td>{r.family_head || "-"}</td>
                              <td>{r.amount ?? "-"}</td>
                              <td>{r.qr_code || "-"}</td>
                              <td>{r.door_no || "-"}</td>
                              <td>{r.water_fee ?? "-"}</td>
                              <td>{r.slrm_fee ?? "-"}</td>
                              <td>{formatIndianDateTime(r.generated_on)}</td>
                              <td>{r.generated_name || r.generated_by || "-"}</td>
                              <td>{r.ward_name || "-"}</td>
                              <td>{r.rct_type || "-"}</td>
                              <td>{r.man_rct_no || "-"}</td>
                              <td>{r.user_type || "-"}</td>
                              <td>{r.office_username || "-"}</td>
                              <td>{r.agent_name || "-"}</td>
                              <td>{r.remarks || "-"}</td>
                              <td>{formatIndianDateTime(r.rct_date)}</td>
                              <td>{r.mode || "-"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}